import { Component, OnInit } from '@angular/core';
import {
  ErrorMessages,
  RTO_DETAILS_CONFIG,
  TeamCompositionListInfo,
} from '@app/shared/constants/constants';
import { SearchService } from '@app/shared/services/search.service';
import { paginationParams } from '@app/shared/config/table.config';
import { RtoDetailsCSS, RtoTypeEnum } from '@app/shared/constants/global.enum';
import { Router } from '@angular/router';
import { columnDefs, exportColumnDefs, rtoLegendItems } from './rto-details.config';
import { TeamManagementService } from '../team-management.service';
import {
  RtoDetails,
  RtoDetailsComments,
  RtoEmployeeMonth,
} from '@app/shared/interfaces/team-management.model';
import { AuthService } from '@app/core/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { AddCommentsComponent } from '@pages/asset-management/cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';
import { AlertService } from '@services/alert.service';
import { dropdownValues } from '@interfaces/common.model';

@Component({
  selector: 'app-rto-details',
  templateUrl: './rto-details.component.html',
  styleUrl: './rto-details.component.scss',
})
export class RtoDetailsComponent implements OnInit {
  errorMsg: string = ErrorMessages.NoDataAvailable;
  imageSrc: string = 'assets/img/no-data-found.png';
  isDataLoaded: boolean = true;
  rtoData: RtoDetails[] = [];
  monthOptions: any[] = [];
  directIndirectReporteeOptions: dropdownValues[] = [];
  displayData: RtoDetails[] = [];
  selectedEmployee: string = 'all';
  selectedDropdownValue: { employeeId: string; filterType?: string } | null =null;
  columnDefs = columnDefs;
  paginationParams = paginationParams;
  rtoLegendItems = rtoLegendItems;
  content: string = '';
  rto = RtoTypeEnum;
  rtoCSS = RtoDetailsCSS;
  filterValue: any;
  showAddExceptionModal = false;
  RTO_DETAILS_CONFIG = RTO_DETAILS_CONFIG;
  selectedOption: string = '';
  loggedInEmployeeCode: string = '';
  month: string = '';
  year: string = '';
  routesEnums = TeamCompositionListInfo;
  showTeamDetailsModal: boolean = false;
  selectedProfileId: string | null = null;
  showCustomDateRange = false;
  customStartDate: string = '';
  customEndDate: string = '';
  selectedRows: RtoDetails[] = [];
  // ── Months multi-select filter ──
  showMonthsPicker = false;
  monthsYearOptions: dropdownValues[] = [];
  selectedMonthsYear: number = new Date().getFullYear();
  monthChips: { label: string; month: number; disabled: boolean }[] = [];
  selectedMonths: Set<string> = new Set(); // 'YYYY-MM' keys, can span years
  appliedMonthsCsv: string = '';
  // ── Per-employee day drill-down panel ──
  showRtoDrill = false;
  drillEmployee: RtoDetails | null = null;
  drillMonths: RtoEmployeeMonth[] = [];
  drillLoading = false;
  expandedDrillMonth: string | null = null;
  weekdayHeaders = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  // in-panel period controls — independent of the table filter so the
  // manager can change the person's window without touching the grid
  drillYear: number = new Date().getFullYear();
  drillMonthChips: { label: string; month: number; disabled: boolean }[] = [];
  drillSelectedMonths: Set<string> = new Set();
  dateLabel = 'Select Date';
  startDatePlaceholder = 'Start Date';
  endDatePlaceholder = 'End Date';

  constructor(
    private router: Router,
    private searchService: SearchService,
    private RtoDetailsService: TeamManagementService,
    private authService: AuthService,
    private dialog: MatDialog,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.loggedInEmployeeCode =
      this.authService.userProfile?.employeeId.toString()!;

    // Current year's months (Jan → current) as direct options; anything
    // older goes through Select Months (any year) or Custom Date Range.
    this.monthOptions = [
      ...this.buildCurrentYearMonthOptions(),
      { label: 'Custom Date Range', value: 'Custom Date Range', selected: false },
      { label: 'Select Months', value: 'Select Months', selected: false },
    ];
    this.initMonthsPicker();

    const navigationSelection =
      this.router.getCurrentNavigation()?.extras.state?.[
        'selectedDropdownValue'
      ] || history.state?.selectedDropdownValue;

    if (navigationSelection) {
      this.selectedDropdownValue = navigationSelection;
      this.selectedEmployee = this.getDropdownValueFromSelection(
        navigationSelection,
      );
      this.RtoDetailsService.setSelectedDropdownValue(navigationSelection);
    }

    this.RtoDetailsService.getSelectedDropdownValue().subscribe((selection) => {
      if (selection) {
        this.selectedDropdownValue = selection;
        this.selectedEmployee = this.getDropdownValueFromSelection(selection);
      }
    });

    this.RtoDetailsService.getReporteeData().subscribe((reporteeData) => {
      if (reporteeData?.length) {
        this.setReporteeOptions(reporteeData);
      }
    });

    this.loadDirectReportees();

    // default to the current month
    const now = new Date();
    this.selectedOption = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.loadRtoDetailsForCurrentSelection(this.getSelectionLabel(this.selectedOption));
  }

  private buildCurrentYearMonthOptions(): { label: string; value: string; selected: boolean }[] {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const options = [];
    for (let m = 0; m <= now.getMonth(); m++) {
      options.push({
        label: `${monthNames[m]} ${now.getFullYear()}`,
        value: `${now.getFullYear()}-${String(m + 1).padStart(2, '0')}`,
        selected: m === now.getMonth(),
      });
    }
    return options;
  }

  /** emerald-dropdown's onChange emits the option LABEL ("Jul 2026") while
   *  onChangeValue emits the VALUE ("2026-07"); some paths also hand over the
   *  option object. Normalise all three to the value so a single month is
   *  recognised (previously the label fell through to 'Last Month'). */
  private resolveMonthOptionValue(event: any): string {
    if (event == null) return '';
    if (typeof event === 'object' && 'value' in event) return String(event.value ?? '');
    const raw = String(event);
    const byValue = this.monthOptions.find((o) => o.value === raw);
    if (byValue) return byValue.value;
    const byLabel = this.monthOptions.find((o) => o.label === raw);
    return byLabel ? byLabel.value : raw;
  }

  /** 'YYYY-MM' single-month option from the main dropdown. */
  private isSingleMonth(value: string): boolean {
    return /^\d{4}-\d{2}$/.test(value || '');
  }

  openAddExceptionModal() {
    this.showAddExceptionModal = true;
  }

  closeAddExceptionModal() {
    this.showAddExceptionModal = false;
  }

  onExceptionAdded(exceptionData: any) {
    const selectionLabel = this.getSelectionLabel(this.selectedOption);
    this.loadRtoDetailsForCurrentSelection(
      selectionLabel,
      this.customStartDate,
      this.customEndDate,
    );
  }

  private getCurrentMonth(): string {
    const currentDate = new Date();
    return String(currentDate.getMonth() + 1).padStart(2, '0');
  }

  private getCurrentYear(): string {
    const currentDate = new Date();
    return String(currentDate.getFullYear());
  }

  getTooltipContent(eachRow: any): string {
    return this.formatComments(eachRow, true);
  }

  searchContractors(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(
      event as KeyboardEvent,
    );
    const filterData = this.rtoData?.map(({ lastHireDate, ...rest }) => rest);
    this.displayData = filterData?.length
      ? this.searchService.getSearchedData(
        filterData,
        this.filterValue?.toLowerCase(),
      )
      : [];
  }
  clearInput() {
    this.filterValue = '';
    this.displayData = [...this.rtoData];
  }

  setMonthAndYear(selectedOption: string): void {
    const [monthValue, yearValue] = selectedOption.split(' ');
    const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
    const formattedMonth = monthStr < 10 ? `0${monthStr}` : `${monthStr}`;
    this.month = formattedMonth;
    this.year = yearValue;
  }

  onMonthChange(event: any): void {
    const selectedValue = this.resolveMonthOptionValue(event);
    this.selectedOption = selectedValue;
    if (selectedValue === 'Custom Date Range') {
      this.showCustomDateRange = true;
      this.showMonthsPicker = false;
      this.appliedMonthsCsv = '';
      this.customStartDate = '';
      this.customEndDate = '';
      return;
    }
    if (selectedValue === 'Select Months') {
      this.showMonthsPicker = true;
      this.showCustomDateRange = false;
      return;
    }
    this.showCustomDateRange = false;
    this.showMonthsPicker = false;
    this.appliedMonthsCsv = '';
    if (this.isSingleMonth(selectedValue)) {
      // keep month/year in sync for the Add Exception modal
      const [y, m] = selectedValue.split('-');
      this.year = y;
      this.month = m;
    }
    const selectionLabel = this.getSelectionLabel(selectedValue);
    this.loadRtoDetailsForCurrentSelection(selectionLabel);
  }

  // ── Months multi-select filter ─────────────────────────────────────────

  private initMonthsPicker(): void {
    const currentYear = new Date().getFullYear();
    this.monthsYearOptions = [];
    for (let y = currentYear; y >= currentYear - 3; y--) {
      this.monthsYearOptions.push({
        label: String(y),
        value: String(y),
        selected: y === this.selectedMonthsYear,
      });
    }
    this.buildMonthChips();
  }

  private buildMonthChips(): void {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.monthChips = monthNames.map((label, i) => ({
      label,
      month: i + 1,
      // future months of the current year can't have attendance yet
      disabled:
        this.selectedMonthsYear === now.getFullYear() && i > now.getMonth(),
    }));
  }

  onMonthsYearChange(event: any): void {
    const value = typeof event === 'object' ? event?.value : event;
    this.selectedMonthsYear = Number(value);
    this.buildMonthChips();
  }

  monthKey(month: number): string {
    return `${this.selectedMonthsYear}-${String(month).padStart(2, '0')}`;
  }

  isMonthSelected(month: number): boolean {
    return this.selectedMonths.has(this.monthKey(month));
  }

  toggleMonthChip(chip: { month: number; disabled: boolean }): void {
    if (chip.disabled) return;
    const key = this.monthKey(chip.month);
    if (this.selectedMonths.has(key)) {
      this.selectedMonths.delete(key);
    } else {
      this.selectedMonths.add(key);
    }
  }

  get selectedMonthsLabel(): string {
    return [...this.selectedMonths]
      .sort()
      .map((key) => {
        const [y, m] = key.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[Number(m) - 1]} ${y}`;
      })
      .join(', ');
  }

  applyMonthsFilter(): void {
    if (this.selectedMonths.size === 0) {
      this.alertService.showWarningAlert(
        'No months selected',
        'Pick at least one month to apply the filter.',
      );
      return;
    }
    this.appliedMonthsCsv = [...this.selectedMonths].sort().join(',');
    this.loadRtoDetailsForCurrentSelection('Selected Months');
  }

  clearMonthsFilter(): void {
    this.selectedMonths.clear();
    this.appliedMonthsCsv = '';
  }

  // ── Per-employee day drill-down panel ──────────────────────────────────

  openRtoDrill(row: RtoDetails): void {
    this.drillEmployee = row;
    this.showRtoDrill = true;
    this.expandedDrillMonth = null;
    this.drillSelectedMonths.clear();

    // Mirror the table's active period so the panel explains the row
    const months = this.activeMonthsCsv();
    const selection = months ? 'Last Month' : this.getSelectionLabel(this.selectedOption);
    const start = this.selectedOption === 'Custom Date Range' ? this.customStartDate : undefined;
    const end = this.selectedOption === 'Custom Date Range' ? this.customEndDate : undefined;
    this.fetchDrillData(row.empId, selection, start, end, months);
  }

  private fetchDrillData(
    empId: string,
    selection: string,
    start?: string,
    end?: string,
    months?: string,
  ): void {
    this.drillLoading = true;
    this.drillMonths = [];
    this.RtoDetailsService.getEmployeeRtoDays(empId, selection, start, end, months).subscribe({
      next: (res) => {
        this.drillMonths = res.data || [];
        this.drillLoading = false;
        if (this.drillMonths.length === 1) {
          this.expandedDrillMonth = this.drillMonths[0].month;
        }
        this.seedDrillPeriodControls();
      },
      error: () => {
        this.drillMonths = [];
        this.drillLoading = false;
      },
    });
  }

  /** Reflect the months just fetched in the panel's own period controls. */
  private seedDrillPeriodControls(): void {
    this.drillSelectedMonths = new Set(this.drillMonths.map((m) => m.month));
    const last = this.drillMonths[this.drillMonths.length - 1];
    this.drillYear = last ? Number(last.month.split('-')[0]) : new Date().getFullYear();
    this.buildDrillMonthChips();
  }

  private buildDrillMonthChips(): void {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.drillMonthChips = monthNames.map((label, i) => ({
      label,
      month: i + 1,
      disabled: this.drillYear === now.getFullYear() && i > now.getMonth(),
    }));
  }

  onDrillYearChange(value: string): void {
    this.drillYear = Number(value);
    this.buildDrillMonthChips();
  }

  drillMonthKey(month: number): string {
    return `${this.drillYear}-${String(month).padStart(2, '0')}`;
  }

  isDrillMonthSelected(month: number): boolean {
    return this.drillSelectedMonths.has(this.drillMonthKey(month));
  }

  toggleDrillChip(chip: { month: number; disabled: boolean }): void {
    if (chip.disabled) return;
    const key = this.drillMonthKey(chip.month);
    if (this.drillSelectedMonths.has(key)) {
      this.drillSelectedMonths.delete(key);
    } else {
      this.drillSelectedMonths.add(key);
    }
  }

  applyDrillPeriod(): void {
    if (!this.drillEmployee) return;
    if (this.drillSelectedMonths.size === 0) {
      this.alertService.showWarningAlert(
        'No months selected',
        'Pick at least one month to load attendance.',
      );
      return;
    }
    const csv = [...this.drillSelectedMonths].sort((a, b) => a.localeCompare(b)).join(',');
    this.expandedDrillMonth = null;
    this.fetchDrillData(this.drillEmployee.empId, 'Last Month', undefined, undefined, csv);
  }

  closeRtoDrill(): void {
    this.showRtoDrill = false;
    this.drillEmployee = null;
    this.drillMonths = [];
    this.expandedDrillMonth = null;
  }

  toggleDrillMonth(month: string): void {
    this.expandedDrillMonth = this.expandedDrillMonth === month ? null : month;
  }

  monthBarPct(m: RtoEmployeeMonth): number {
    const denominator = Math.max(m.workingDays, m.officeDays, 1);
    return Math.min(100, Math.round((m.officeDays / denominator) * 100));
  }

  get drillTotalDays(): number {
    return this.drillMonths.reduce((sum, m) => sum + m.officeDays, 0);
  }

  /** Mon-first calendar weeks for a drill month; attended dates marked. */
  calendarWeeks(m: RtoEmployeeMonth): { day: number | null; attended: boolean }[][] {
    const [year, month] = m.month.split('-').map(Number);
    const attended = new Set(m.dates.map((d) => Number(d.split('-')[2])));
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Mon=0

    const cells: { day: number | null; attended: boolean }[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push({ day: null, attended: false });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, attended: attended.has(d) });
    while (cells.length % 7 !== 0) cells.push({ day: null, attended: false });

    const weeks: { day: number | null; attended: boolean }[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case this.rto.Regular.toLowerCase():
        return this.rtoCSS.statusRegular;
      case this.rto.Remote.toLowerCase():
        return this.rtoCSS.statusRemote;
      case this.rto.Hybrid.toLowerCase():
        return this.rtoCSS.statusHybrid;
      default:
        return this.rtoCSS.statusPartialHybrid;
    }
  }

  navigateToTeamMgmtInfo(empId?: string): void {
    let idToUse = empId;
    if (idToUse) {
      this.router.navigate([TeamCompositionListInfo.teamManagementinfo], {
        state: { empId: idToUse },
      });
    }
  }

  goToDashboard(): void {
    this.router.navigate([RTO_DETAILS_CONFIG.teamManagement]);
  }

  rtoDetailsData(
    employeeId: string,
    selection: string,
    startDate?: string,
    endDate?: string,
    filterType?: string,
    months?: string,
  ) {
    this.RtoDetailsService.getRtoDetails(
      employeeId,
      // BE ignores selection when months CSV is present, but it must still
      // be a value RTODateUtils understands for the no-months path
      months ? 'Last Month' : selection,
      startDate,
      endDate,
      filterType,
      months,
    ).subscribe({
      next: (res) => {
        this.rtoData = res.data?.map((item) => ({
          ...item,
          rtoPercentage: Math.round(Number(item.rtoPercentage) * 100) / 100,
        })) || [];
        this.displayData = [...this.rtoData];
      },
      error: (error) => {
        console.error('API error:', error);
        this.rtoData = [];
        this.displayData = [];
      }
    });
  }
  openTeamDetailsModal(empId?: string) {
    let idToUse = empId;
    if (!idToUse) {
      idToUse = this.rtoData?.[0]?.empId;
    }
    if (idToUse) {
      this.selectedProfileId = idToUse;
      this.showTeamDetailsModal = true;
    }
  }

  closeTeamDetailsModal() {
    this.showTeamDetailsModal = false;
    this.selectedProfileId = null;
  }

  onRowSelected(selectedRows: RtoDetails[]): void {
    this.selectedRows = selectedRows;
  }

  handleExport(): void {
    if (!this.selectedRows || this.selectedRows.length === 0) {
      this.alertService.showWarningAlert(
        "You haven't selected any profiles.",
        'Please select profiles to proceed with the download.',
      );
      return;
    }
    this.openDownloadConfirmDialog();
  }

  private openDownloadConfirmDialog(): void {
    const dataWithComments = this.selectedRows.map(row => ({
      ...row,
      comments: this.formatComments(row, false)
    }));

    this.dialog.open(AddCommentsComponent, {
      data: {
        count: this.selectedRows.length,
        option: 'download',
        selectedCard: 'rto-details',
        selectedRows: dataWithComments,
        columns: exportColumnDefs,
      },
    });
  }

  private formatComments(row: any, forTooltip: boolean): string {
    if (!row.exceptionList?.length) return '';
    return row.exceptionList
      ?.map((comment: RtoDetailsComments) => {
        if (forTooltip) {
          return `𝐃𝐚𝐭𝐞 𝐑𝐚𝐧𝐠𝐞:\n ${comment?.start_date} - ${comment?.end_date}\n𝐑𝐞𝐚𝐬𝐨𝐧: ${comment?.comment}`;
        } else {
          return `Date Range: ${comment?.start_date} - ${comment?.end_date}, Reason: ${comment?.comment}`;
        }
      })
      .join('\n\n');
  }

  private getSelectionLabel(value: string): string {
    const option = this.monthOptions.find((opt) => opt.value === value);
    return option?.label || 'Last Month';
  }

  handleDateRangeChange(filteredData: any) {
    this.customStartDate = filteredData.startDate;
    this.customEndDate = filteredData.endDate;

    this.loadRtoDetailsForCurrentSelection(
      'Custom Date Range',
      filteredData.startDate,
      filteredData.endDate,
    );
  }

  loadDirectReportees(): void {
    this.RtoDetailsService.getReporteesList(this.loggedInEmployeeCode)
      .subscribe({
        next: (response) => {
          const reporteeOptions =
            this.RtoDetailsService.transformReporteesToDropdownOptions(
              response,
            );
          this.RtoDetailsService.setReporteeData(reporteeOptions);
          this.setReporteeOptions(reporteeOptions);
        },
        error: (error) => {
          this.directIndirectReporteeOptions = [
            { label: 'All Teams', value: 'all', selected: true }
          ];
          this.selectedEmployee = 'all';
        }
      });
  }

  onReporteesChange(event: any): void {
    const selectedValue = event?.value || event;
    this.selectedEmployee = selectedValue;
    this.setReporteeOptions(this.directIndirectReporteeOptions);

    if (selectedValue === 'dr') {
      this.RtoDetailsService.setSelectedDropdownValue({
        employeeId: this.loggedInEmployeeCode,
        filterType: 'DirectReportee',
      });
    } else if (selectedValue === 'all') {
      this.RtoDetailsService.setSelectedDropdownValue({
        employeeId: this.loggedInEmployeeCode,
      });
    } else {
      this.RtoDetailsService.setSelectedDropdownValue({
        employeeId: selectedValue,
      });
    }

    this.rtoData = [];
    this.displayData = [];

    if (
      this.selectedOption === 'Custom Date Range' &&
      this.customStartDate &&
      this.customEndDate
    ) {
      this.loadRtoDetailsForCurrentSelection(
        'Custom Date Range',
        this.customStartDate,
        this.customEndDate,
      );
      return;
    }

    const selectionLabel = this.getSelectionLabel(this.selectedOption);
    this.loadRtoDetailsForCurrentSelection(selectionLabel);
  }

  private getDropdownValueFromSelection(
    selection: { employeeId: string; filterType?: string } | null,
  ): string {
    if (selection?.filterType === 'DirectReportee') {
      return 'dr';
    }

    if (!selection || selection.employeeId === this.loggedInEmployeeCode) {
      return 'all';
    }

    return selection.employeeId;
  }

  private setReporteeOptions(options: dropdownValues[]): void {
    const selectedValue = this.getDropdownValueFromSelection(
      this.selectedDropdownValue,
    );

    const hasSelectedOption = options.some(
      (option) => String(option.value) === String(selectedValue),
    );

    this.selectedEmployee = hasSelectedOption ? selectedValue : 'all';
    this.directIndirectReporteeOptions = options.map((option) => ({
      ...option,
      selected: String(option.value) === String(this.selectedEmployee),
    }));
  }

  private getSelectedEmployeeParams(): {
    employeeId: string;
    filterType?: string;
  } {
    if (this.selectedEmployee === 'dr') {
      return {
        employeeId: this.loggedInEmployeeCode,
        filterType: 'DirectReportee',
      };
    }

    if (this.selectedEmployee === 'all') {
      return {
        employeeId: this.loggedInEmployeeCode,
      };
    }

    return {
      employeeId: this.selectedEmployee,
    };
  }

  private loadRtoDetailsForCurrentSelection(
    selection: string,
    startDate?: string,
    endDate?: string,
  ): void {
    const { employeeId, filterType } = this.getSelectedEmployeeParams();
    const months = this.activeMonthsCsv();

    this.rtoDetailsData(employeeId, selection, startDate, endDate, filterType, months);
  }

  /** Months CSV for the active filter: a single dropdown month, or the
   *  applied multi-select; undefined for custom date range. */
  private activeMonthsCsv(): string | undefined {
    if (this.isSingleMonth(this.selectedOption)) return this.selectedOption;
    if (this.selectedOption === 'Select Months' && this.appliedMonthsCsv) {
      return this.appliedMonthsCsv;
    }
    return undefined;
  }
}
